import { useEffect, useRef, useState } from 'react';
import { Plus, Play, Square, Trash2, BarChart3, Edit, Search, Folder, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Poll } from '@yawi/types';
import type { CreatePollDTO } from '@yawi/domain';
import { useServices, useToast, useSection } from '@/app/providers';
import { useI18n } from '@/shared/i18n';
import { useDateFormatter } from '@/shared/hooks';
import { Loading } from '@/core/ui/components/Loading';
import { useDeleteConfirmation } from '@/app/providers';
import { Button } from '@/presentation/components/atoms/Button';
import { PollFormModal } from './PollFormModal';
import { PollResults } from './PollResults';
import { useAbility } from '@/modules/identity/application/ability';
import { SectionSelector } from '@/modules/sections/ui/components/SectionSelector';

type StatusFilter = 'ALL' | 'DRAFT' | 'ACTIVE' | 'CLOSED';

export const AdminPollList = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { formatDate } = useDateFormatter();
  const { confirmDelete } = useDeleteConfirmation();
  const { pollRepo } = useServices();
  const { selectedSectionId, hasSections } = useSection();
  const ability = useAbility();
  const canManagePoll = ability.can('manage', 'all') || ability.can('manage', 'Poll');
  const canCreatePoll = canManagePoll || ability.can('create', 'Poll');
  const canUpdatePoll = canManagePoll || ability.can('update', 'Poll');
  const canDeletePoll = canManagePoll || ability.can('delete', 'Poll');
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPoll, setEditingPoll] = useState<Poll | null>(null);
  const [isRecreating, setIsRecreating] = useState(false);
  const [viewingResults, setViewingResults] = useState<Poll | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
    variant?: 'success' | 'danger' | 'info';
    confirmText: string;
  } | null>(null);

  // Debounce: sin él cada tecla dispara una petición.
  const [debouncedFilter, setDebouncedFilter] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(filter.trim()), 400);
    return () => clearTimeout(timer);
  }, [filter]);

  // Solo la última petición escribe el estado: una respuesta lenta de una
  // búsqueda anterior no puede pisar los resultados de la actual.
  const requestIdRef = useRef(0);

  const loadPolls = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const response = await pollRepo.getPolls({
        page,
        limit: 12,
        search: debouncedFilter || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        sectionId: selectedSectionId ?? undefined,
      });
      if (requestId !== requestIdRef.current) return;
      setPolls(response.data);
      setTotalPages(response.meta.totalPages);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error loading polls:', e);
      toast.error(e instanceof Error && e.message ? e.message : t('common.error'));
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadPolls();
  }, [page, debouncedFilter, statusFilter, selectedSectionId]);

  const handleCreate = async (data: CreatePollDTO) => {
    if (!canCreatePoll) return;
    await pollRepo.createPoll(data);
    toast.success(t('common.savedSuccessfully'));
    loadPolls();
  };

  const handleUpdate = async (id: string, data: CreatePollDTO) => {
    if (!canUpdatePoll) return;
    await pollRepo.updatePoll(id, {
      title: data.title,
      description: data.description,
      startsAt: data.startsAt,
      endsAt: data.endsAt,
      sectionIds: data.sectionIds,
    });
    toast.success(t('common.savedSuccessfully'));
    loadPolls();
  };

  const handleSave = async (data: CreatePollDTO, pollId?: string) => {
    const idToUse = pollId || editingPoll?.id;
    if (idToUse) {
      await handleUpdate(idToUse, data);
    } else {
      await handleCreate(data);
    }
  };

  const handlePublish = (poll: Poll) => {
    if (!canUpdatePoll) return;
    setConfirmModal({
      isOpen: true,
      title: t('polls.admin.publish'),
      message: `${t('poll.admin.publish.message')} ${poll.title}?`,
      variant: undefined,
      confirmText: t('polls.admin.publish'),
      action: async () => {
        await pollRepo.publishPoll(poll.id);
        toast.success(t('polls.admin.published'));
        loadPolls();
      }
    });
  };

  const handleClose = (poll: Poll) => {
    if (!canUpdatePoll) return;
    setConfirmModal({
      isOpen: true,
      title: t('polls.admin.close'),
      message: `${t('poll.admin.close.message')} ${poll.title}?`,
      variant: undefined,
      confirmText: t('polls.admin.close'),
      action: async () => {
        await pollRepo.closePoll(poll.id);
        toast.success(t('polls.admin.closed'));
        loadPolls();
      }
    });
  };

  const handleEditWithConfirmation = (poll: Poll) => {
    if (!canUpdatePoll) return;
    setConfirmModal({
      isOpen: true,
      title: t('common.edit'),
      message: `${t('poll.admin.edit.message')} "${poll.title}"?`,
      variant: undefined,
      confirmText: t('common.edit'),
      action: async () => {
        setEditingPoll(poll);
        setIsRecreating(false);
        setShowModal(true);
      }
    });
  };

  const handleRecreate = (poll: Poll) => {
    if (!canCreatePoll) return;
    const pollToRecreate: Poll = {
      ...poll,
      id: '',
      status: 'DRAFT',
    };
    setEditingPoll(pollToRecreate);
    setIsRecreating(true);
    setShowModal(true);
  };

  const handleDelete = async (poll: Poll) => {
    if (!canDeletePoll) return;
    confirmDelete({
      itemName: poll.title,
      itemType: 'Encuesta',
      onConfirm: async () => {
        try {
          await pollRepo.deletePoll(poll.id);
          toast.success(t('polls.admin.deleted'));
          loadPolls();
        } catch (e) {
          toast.error(t('common.error'));
        }
      },
    });
  };

  const getStatusBadge = (status: Poll['status']) => {
    const styles = {
      DRAFT: 'bg-slate-100 text-slate-600',
      ACTIVE: 'bg-emerald-100 text-emerald-700',
      CLOSED: 'bg-red-100 text-red-700',
    };
    const labels = {
      DRAFT: t('polls.status.draft'),
      ACTIVE: t('polls.status.active'),
      CLOSED: t('polls.status.closed'),
    };
    return (
      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getStatusColor = (status: Poll['status']) => {
    switch (status) {
      case 'DRAFT': return 'bg-slate-100 text-slate-600';
      case 'ACTIVE': return 'bg-emerald-100 text-emerald-700';
      case 'CLOSED': return 'bg-red-100 text-red-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('polls.admin.title')}</h2>
          <p className="text-slate-500 text-sm">Crea y administra encuestas para la comunidad.</p>
        </div>
        {canCreatePoll && (
          <Button
            onClick={() => {
              setEditingPoll(null);
              setIsRecreating(false);
              setShowModal(true);
            }}
            icon={<Plus size={16} />}
          >
            {t('polls.admin.create')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="relative flex-1">
          <input
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-100"
            placeholder="Buscar encuesta..."
            value={filter}
            onChange={(e) => {
              setPage(1);
              setFilter(e.target.value);
            }}
          />
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
        </div>
        {hasSections && (
          <SectionSelector
            variant="slate"
            className="w-48"
            onSelectionChange={() => setPage(1)}
          />
        )}
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {(['ALL', 'DRAFT', 'ACTIVE', 'CLOSED'] as StatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => {
                setPage(1);
                setStatusFilter(status);
              }}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                statusFilter === status
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
              }`}
            >
              {status === 'ALL' ? 'Todos' : t(`polls.status.${status.toLowerCase()}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <Loading />
      ) : polls.length === 0 ? (
        <div className="text-center p-12 border-2 border-dashed border-slate-200 rounded-xl">
          <Folder size={48} className="mx-auto mb-2" />
          <p className="text-slate-500">{t('polls.empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {polls.map((poll) => (
            <div
              key={poll.id}
              className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-all group flex flex-col"
            >
              <div className="flex justify-between items-start mb-4">
                <div className={`p-3 rounded-lg ${getStatusColor(poll.status)}`}>
                  <BarChart3 size={24} />
                </div>
                <div className="flex items-center gap-1">
                  {poll.status === 'DRAFT' && canUpdatePoll && (
                    <>
                      <button
                        onClick={() => handleEditWithConfirmation(poll)}
                        className="hover:text-violet-500 transition-colors p-1"
                        title={t('common.edit')}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handlePublish(poll)}
                        className="hover:text-emerald-500 transition-colors p-1"
                        title={t('polls.admin.publish')}
                      >
                        <Play size={16} />
                      </button>
                    </>
                  )}
                  {poll.status === 'ACTIVE' && canUpdatePoll && (
                    <button
                      onClick={() => handleClose(poll)}
                      className="hover:text-red-500 transition-colors p-1"
                      title={t('polls.admin.close')}
                    >
                      <Square size={16} />
                    </button>
                  )}
                  {poll.status === 'CLOSED' && canCreatePoll && (
                    <button
                      onClick={() => handleRecreate(poll)}
                      className="hover:text-emerald-500 transition-colors p-1"
                      title={t('polls.admin.recreate')}
                    >
                      <RotateCcw size={16} />
                    </button>
                  )}
                  {(poll.status === 'DRAFT' || poll.status === 'CLOSED') && canDeletePoll && (
                    <button
                      onClick={() => handleDelete(poll)}
                      className="hover:text-red-500 transition-colors p-1"
                      title={t('common.delete')}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              <h4 className="font-bold text-slate-800 mb-1 line-clamp-2 flex-1" title={poll.title}>
                {poll.title}
              </h4>

              {poll.description && (
                <p className="text-xs text-slate-500 mb-3 line-clamp-2">{poll.description}</p>
              )}

              <div className="flex items-center gap-2 mb-4">
                {getStatusBadge(poll.status)}
                <span className="text-xs text-slate-400">• {poll.options.length} opciones</span>
              </div>

              <div className="mt-auto pt-4 border-t border-slate-50 flex justify-between items-center">
                <span className="text-xs text-slate-400">
                  {t('polls.ends')}: {formatDate(poll.endsAt)}
                </span>
                {(poll.status === 'ACTIVE' || poll.status === 'CLOSED') && (
                  <button
                    onClick={() => setViewingResults(poll)}
                    className="flex items-center gap-1 text-xs font-bold text-violet-600 hover:text-violet-800 transition-colors bg-violet-50 px-3 py-1.5 rounded-lg"
                  >
                    <BarChart3 size={14} /> Ver
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40"
            aria-label="Página anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-slate-500">Página {page} de {totalPages}</span>
          <button
            type="button"
            onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40"
            aria-label="Página siguiente"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {showModal && ((editingPoll && !isRecreating && canUpdatePoll) || (isRecreating && canCreatePoll) || (!editingPoll && canCreatePoll)) && (
        <PollFormModal
          poll={editingPoll}
          isRecreating={isRecreating}
          onClose={() => {
            setShowModal(false);
            setEditingPoll(null);
            setIsRecreating(false);
          }}
          onSave={handleSave}
        />
      )}

      {viewingResults && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4">
              <PollResults poll={viewingResults} />
              <button
                onClick={() => setViewingResults(null)}
                className="mt-4 w-full py-2 text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal?.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm animate-in zoom-in-95 duration-200">
            <div className="p-6">
              {confirmModal.variant && (
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                  confirmModal.variant === 'success' ? 'bg-emerald-100 text-emerald-600' :
                  confirmModal.variant === 'danger' ? 'bg-red-100 text-red-600' :
                  'bg-violet-100 text-violet-600'
                }`}>
                  {confirmModal.variant === 'success' ? <Play size={24} className="ml-1" /> :
                   confirmModal.variant === 'danger' ? <Square size={24} /> :
                   <Edit size={24} />}
                </div>
              )}
              <h3 className="text-lg font-bold text-slate-800 mb-2">{confirmModal.title}</h3>
              <p className="text-slate-500 text-sm mb-6">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal(null)}
                  className="flex-1 px-4 py-2 text-slate-600 font-medium bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={async () => {
                    try {
                      await confirmModal.action();
                    } catch (e) {
                      toast.error(t('common.error'));
                    }
                    setConfirmModal(null);
                  }}
                  className={`flex-1 px-4 py-2 text-white font-medium rounded-lg transition-colors ${
                    confirmModal.variant === 'success' ? 'bg-emerald-600 hover:bg-emerald-700' :
                    confirmModal.variant === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-violet-600 hover:bg-violet-700'
                  }`}
                >
                  {confirmModal.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
